import { useMediaQuery, useTheme } from '@material-ui/core';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { RootState } from '../../store';
import {
  setSecondarySidebarHidden,
  toggleSecondarySidebar,
} from '../../store/ui/actions';
import BaseChannelSidebar from '../Channels/BaseChannelSidebar';
import { AvatarInfoType } from '../Channels/ChannelSidebarItem.v2';
import { ChannelsPageLayoutHeaderContext } from '../Layout';
import { InboxSidebarItem } from './InboxSidebarItem';
import { NotificationGroups } from '../../store/notifications/types';
import { InboxPageContext } from '../../context/inboxPageContext';
import { useGetInboxNotificationsQuery } from '../../services/api/inboxApi';
import { getNotificationReadStatus } from '../../utils/NotificationsUtils';
import { INBOX_PAGE } from '../../constants';

export enum InboxNotificationStatus {
  Read = 'read',
  Sent = 'sent',
}
export interface InboxSidebarItemData {
  id: string;
  label: string;
  description: string;
  notificationStatus: InboxNotificationStatus;
  avatarInfo: (AvatarInfoType & { email: string }) | null;
  notificationGroup: NotificationGroups;
  channelId: string;
  data: string;
}

export const InboxSidebar = () => {
  const dispatch = useDispatch();

  const { data: inboxNotifications } = useGetInboxNotificationsQuery();

  const secondarySidebarHidden = useSelector(
    (state: RootState) => state.ui.secondarySideBar.hidden,
  );
  const searchKey = useSelector((state: RootState) => state.ui.searchValue);
  const { setSelectedNotification, selectedNotification } = React.useContext(
    InboxPageContext,
  );
  const clients = useSelector((state: RootState) => state.clients.clients);
  const internalUsers = useSelector(
    (state: RootState) => state.users.internalUsers,
  );
  const companies = useSelector((state: RootState) => state.clients.companies);

  // all users ( internal + client )
  const allUsers = [...clients, ...internalUsers];

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('xs'));

  const {
    setCreateButtonProps,
    setChannelsContentToolbarHidden,
    setActiveChannelTitle,
  } = React.useContext(ChannelsPageLayoutHeaderContext);

  // on click we want to set secondary sidebar info as notification id
  const onSelectChannel = (sidebarItemOption: InboxSidebarItemData) => {
    setActiveChannelTitle(sidebarItemOption.label);
    setChannelsContentToolbarHidden(false);
    dispatch(
      toggleSecondarySidebar({
        isOpen: !isMobileScreen,
      }),
    );
    setSelectedNotification(sidebarItemOption);
  };

  React.useEffect(
    () => () => {
      // reset inbox sidebar
      setSelectedNotification(null);
    },
    [],
  );

  const formattedNotificationsList = React.useMemo(() => {
    if (!inboxNotifications) return [];

    const notifications: InboxSidebarItemData[] = inboxNotifications
      .slice() // creating copy of array
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) // latest notification first
      .map(notification => {
        const { fields } = notification;

        const notificationGroup =
          fields?.groupingEntityType || fields?.notificationGroup || '';

        const sender = allUsers.find(c => c.id === fields.senderUserId);

        // notification sender name
        // incase user is deleted show sender name as 'Deleted User'
        const senderName = sender
          ? `${sender.fields.given_name} ${sender.fields.family_name}`
          : 'Deleted User';

        let label = '';

        // generate notification label
        // based on the notification group
        switch (notificationGroup) {
          case 'NOTIFY_FILE_UPLOADED': {
            const companyId = sender?.fields.companyId;
            const senderCompanyName =
              companyId && companies.find(c => c.id === companyId)?.fields.name;
            // if sender belongs to a company add company name in notification label
            if (senderCompanyName) {
              label = `${senderName} from ${senderCompanyName}`;
            } else {
              label = `${senderName}`;
            }
            // 'file' for count = 1, 'files' for count > 1
            label = `${label} uploaded ${fields.count} file${
              fields.count > 1 ? 's' : ''
            }`;
            break;
          }

          case 'esigRequestCompleteNotifications': {
            label = `${fields?.itemName ||
              'File'} has been signed by all parties`;
            break;
          }

          default:
            label = '';
        }

        const notificationStatus = getNotificationReadStatus(notification);

        const description = moment(notification.createdAt).fromNow();

        const avatarInfo = {
          name: senderName || '',
          imageUrl: sender?.fields ? sender?.fields.picture : '',
          fallbackColor: sender?.fields ? sender.fields.fallbackColor : '',
          email: sender?.fields ? sender.fields.email : '',
        };

        return {
          id: notification.id,
          label,
          notificationStatus,
          description,
          avatarInfo,
          notificationGroup,
          channelId: fields.channelId,
          data: notification.data,
        };
      });

    return notifications;
  }, [inboxNotifications]);

  // initial load settings
  React.useEffect(() => {
    // we don't need create button on inbox sidebar
    setCreateButtonProps(null);
    setChannelsContentToolbarHidden(true);
  }, []);

  React.useEffect(() => {
    // when there is no notification ( unread + read)
    if (!inboxNotifications?.length) {
      if (isSmallScreen) {
        setActiveChannelTitle(INBOX_PAGE.label); // page title on mobile
      }

      // display content toolbar on small screens
      // to display page title
      setChannelsContentToolbarHidden(!isSmallScreen);
      // don't show inbox sidebar
      dispatch(setSecondarySidebarHidden(true));
      dispatch(toggleSecondarySidebar({ isOpen: false }));
    } else {
      dispatch(setSecondarySidebarHidden(false));
      dispatch(toggleSecondarySidebar({ isOpen: true }));
      setChannelsContentToolbarHidden(!isMobileScreen);
    }
  }, [isMobileScreen, isSmallScreen, inboxNotifications]);

  const deSelectNotification = () => {
    setActiveChannelTitle('');
    setChannelsContentToolbarHidden(true);
    setSelectedNotification(null);
  };

  const filteredNotificitionList = React.useMemo(() => {
    const notificationList = [...formattedNotificationsList];
    // if search value is empty then return all the notifications available in the list
    if (!searchKey) return notificationList;

    const queriedNotifications = notificationList.filter(n =>
      n.label.toLowerCase().includes(searchKey.toLowerCase()),
    );

    // if the selected notification is not included in the queried result
    // de select the notification
    if (selectedNotification) {
      const selectedNotificationExist = queriedNotifications.find(
        n => n.id === selectedNotification.id,
      );
      if (!selectedNotificationExist) {
        deSelectNotification();
      }
    }
    // return only those notifications that meet our search criteria
    return queriedNotifications;
  }, [searchKey, formattedNotificationsList, selectedNotification]);

  if (secondarySidebarHidden) return null;

  return (
    <BaseChannelSidebar
      entity="inbox"
      sidebarItemOptions={filteredNotificitionList}
      onSelectChannel={onSelectChannel}
      itemRenderer={InboxSidebarItem}
      noCreateAction
      noSpace
      shouldSetQueryParam={false}
      disableDefaultSelect
    />
  );
};
